/**
 * Hold empty "Thinking…" / "Working…" chrome until a run has actually started
 * streaming or this delay has elapsed. Instant slash replies (`/help`, `/status`)
 * finish before this, so they never flash a run card.
 */
export const LIVE_RUN_INDICATOR_DELAY_MS = 200;

/** Format elapsed wait time for Fleet Control (live counter while agent runs). */
export function formatChatWaitElapsed(startedAt: number, nowMs: number): string {
  const ms = Math.max(0, nowMs - startedAt);
  if (ms < 60_000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) {
    return `${min}:${String(sec).padStart(2, "0")}`;
  }
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return `${hr}:${String(remMin).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export function isChatWaitActive(params: {
  sending: boolean;
  canAbort?: boolean;
  stream: string | null;
  streamStartedAt: number | null;
}): boolean {
  if (params.streamStartedAt == null) {
    return false;
  }
  return params.sending || Boolean(params.canAbort) || params.stream !== null;
}

/**
 * How long the live-run bubble stays visible after an injected message
 * (chat.inject from a subagent announce) lands. Without this window the
 * bubble never appears for injects, because `isChatWaitActive` requires
 * `sending`/`canAbort`/`stream` which are all false on the inject path.
 * 1800ms covers the broadcast → Lit updated() → DOM append → browser scroll
 * settle window (operator reported the 800ms ceiling as too short: the bubble
 * disappeared before the row registered visually, so the chat kept jumping).
 * The window is short enough not to feel sticky across normal user activity.
 * JAISIU-XXXX (operator ask Sep-10: "show activity until the model stops").
 */
export const INJECT_BUBBLE_FLASH_MS = 1800;

/**
 * True when the thread / compose chrome should show an in-progress run.
 * Stream tokens and tool rows appear immediately; the empty thinking card
 * waits {@link LIVE_RUN_INDICATOR_DELAY_MS} so `/help` does not flicker.
 *
 * JAISIU-activity-always-visible — also returns true while ANY subagent
 * is active, even when the parent chat.run is idle. Pre-fix this only
 * looked at `sending` / `canAbort` / `stream` — none of which are set
 * for a parent that already returned its first response and is just
 * waiting on `sessions_spawn` children. Operators reported "the new
 * sessions don't appear to be doing anything" because the live-run
 * chrome disappeared the instant the parent's chat.final landed, even
 * though 4 sibling subagents were mid-run.
 */
export function shouldShowLiveRunIndicator(params: {
  sending: boolean;
  canAbort?: boolean;
  stream: string | null;
  streamStartedAt: number | null;
  nowMs: number;
  toolMessageCount?: number;
  /**
   * JAISIU-XXXX: timestamp at which an injected fleet-announce message just
   * landed. While `nowMs - injectedAt < INJECT_BUBBLE_FLASH_MS`, the bubble
   * stays visible regardless of `sending`/`canAbort`/`stream`. The chat
   * controller clears this via setTimeout after the flash window.
   */
  injectedAt?: number | null;
  /**
   * JAISIU-activity-always-visible — true when at least one
   * subagent fan-out child is still active. Drives the chrome for
   * "parallel subagent research" use cases where the parent has
   * already responded but the children are still running.
   */
  subagentLiveCount?: number;
}): boolean {
  // JAISIU-activity-always-visible — parallel subagent fan-out.
  // When the parent chat.run has finished (final landed, no more
  // stream) but 1+ sibling subagents are still running, the
  // operator must still see the activity chrome. Otherwise the
  // session row collapses to a static "previous response" view
  // while heavy research work is silently happening in the
  // background. The `subagentLiveCount > 0` check covers both the
  // "fan-out in flight" and the "fan-out mid-run" cases.
  const subagentLiveCount = params.subagentLiveCount ?? 0;
  if (subagentLiveCount > 0) {
    return true;
  }
  // JAISIU-XXXX: inject flash path. Checked BEFORE the isChatWaitActive
  // gate because injects never set `sending` / `canAbort` / `stream` —
  // they arrive as a single `chat.final` outside any in-flight run. The
  // brief flash window lets the operator see "something just landed"
  // before the row scrolls into view.
  const injectedAt = params.injectedAt ?? null;
  if (typeof injectedAt === "number" && params.nowMs - injectedAt < INJECT_BUBBLE_FLASH_MS) {
    return true;
  }
  // Require an active wait first. Leftover tool rows or stream fragments
  // must not keep the live "measuring time" bubble open after the run ends.
  if (!isChatWaitActive(params)) {
    return false;
  }
  const streamText = typeof params.stream === "string" ? params.stream.trim() : "";
  if (streamText.length > 0 || (params.toolMessageCount ?? 0) > 0) {
    return true;
  }
  const startedAt = params.streamStartedAt;
  if (startedAt == null) {
    return false;
  }
  return params.nowMs - startedAt >= LIVE_RUN_INDICATOR_DELAY_MS;
}
